// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';
import type { TraderOrderPayload } from '@shared/data-access/interfaces/api/ai/trader';
import type {
  BaseOrder,
  Order,
} from '@shared/data-access/interfaces/api/brokerage/commons';
import type { LatestPricesResp } from '@shared/data-access/interfaces/api/commons';
import type { TradeDetails as Trade } from '@shared/data-access/interfaces/redux/trades';
import { formatDate } from '@shared/ui-kit/utils/format-date';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  selectAccountId,
  selectBrokerConfiguration,
} from '@advisor-app/store/slices/broker';
import { setTrades } from '@advisor-app/store/slices/trader';
import { delay } from '../utils';
import { traderApi } from '@advisor-app/store/api/ai/trader-api';

export interface OrderStatus {
  ticker: string;
  qty: number;
  status: string;
  volume: number;
  logo: string;
  operation: string;
}

export const useExecuteTrades = (trades: Trade[]) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState<OrderStatus[]>([]);
  const broker = useAppSelector(selectBrokerConfiguration);
  const brokerAccountId = useAppSelector(selectAccountId);

  // calculate the orders based on the latest prices
  const getOrders = useCallback(
    async (prices: LatestPricesResp[]): Promise<BaseOrder[]> => {
      const payload: TraderOrderPayload = {
        latest_prices: prices,
        trades,
      };
      const ordersResp = await dispatch(
        traderApi.endpoints.order.initiate({ payload }),
      ).unwrap();
      const orders = ordersResp.map(order => {
        return {
          ticker: order.ticker,
          stocks_number: order.contracts_number,
          side: order.operation.toLowerCase(),
        };
      });

      // sort orders by sell first then buy orders
      const sortedOrders = orders.sort(
        /* istanbul ignore next */ a => (a.side === 'sell' ? -1 : 1),
      );
      return sortedOrders;
    },
    [dispatch, trades],
  );

  const executeOrders = useCallback(
    async (orders: BaseOrder[]) => {
      const resp = await dispatch(
        portfolioApi.endpoints.postOrders.initiate({
          params: broker,
          payload: {
            orders,
            account_id: brokerAccountId,
          },
        }),
      ).unwrap();
      return resp;
    },
    [broker, brokerAccountId, dispatch],
  );

  const checkOrdersStatus = useCallback(
    async (executedOrders: Order[]) => {
      // await 1.5 seconds then check the orders status
      await delay(1500);

      const today = new Date();
      const tomorrow = new Date(new Date().setDate(today.getDate() + 1));
      const todayIso = today.toISOString().slice(0, 10);
      const tomorrowIso = tomorrow.toISOString().slice(0, 10);
      const order_filter = {
        start_date: formatDate(todayIso, 'standard', 'en') ?? todayIso,
        end_date: formatDate(tomorrowIso, 'standard', 'en') ?? tomorrowIso,
      };

      const todayOrders = await dispatch(
        portfolioApi.endpoints.ordersSearch.initiate({
          params: broker,
          payload: {
            order_filter,
            account_id: brokerAccountId,
          },
        }),
      ).unwrap();

      const executedOrdersIds = executedOrders.map(({ order_id }) => order_id);
      const executedOrdersStatus = todayOrders.filter(({ order_id }) =>
        executedOrdersIds.includes(order_id),
      );
      return executedOrdersStatus;
    },
    [broker, brokerAccountId, dispatch],
  );

  const updateOrdersStatus = useCallback(
    (executedOrders: Order[]) => {
      const updatedOrders = trades.map(trade => {
        /* istanbul ignore next */
        const executedOrder = executedOrders.find(
          executedOrder => executedOrder.ticker === trade.ticker,
        );

        return {
          ticker: trade.ticker,
          qty: executedOrder?.stocks_number || trade.contracts_number,
          status: executedOrder?.status || 'error',
          volume: trade.expected_volume,
          logo: trade.columnName?.url_logo as string,
          operation: trade.operation.toLocaleLowerCase(),
        };
      });
      setOrders(updatedOrders);
    },
    [trades],
  );

  const setInitialOrders = useCallback(() => {
    const orders = trades.map(trade => {
      return {
        ticker: trade.ticker,
        qty: trade.contracts_number,
        status: 'pending',
        volume: trade.expected_volume,
        logo: trade.columnName?.url_logo as string,
        operation: trade.operation.toLocaleLowerCase(),
      };
    });
    setOrders(orders);
  }, [trades]);

  const updateTrader = useCallback(
    (executedOrders: Order[]) => {
      const failedOrders = executedOrders.filter(order => order.status === 'error');
      /* istanbul ignore next */
      const failedTrades = failedOrders
        .map(order =>
          trades.find(
            trade =>
              trade.ticker === order.ticker &&
              trade.operation === order.side.toUpperCase(),
          ),
        )
        .filter(Boolean) as Trade[];

      dispatch(setTrades(failedTrades));
    },
    [dispatch, trades],
  );

  const executeTrades = useCallback(
    async (prices: LatestPricesResp[]) => {
      setInitialOrders();
      setOpen(true);
      setLoading(true);
      const orders = await getOrders(prices);
      const executedOrders = await executeOrders(orders);
      const executedOrdersStatus = await checkOrdersStatus(executedOrders);
      updateOrdersStatus(executedOrdersStatus);
      updateTrader(executedOrders);
      setLoading(false);
    },
    [
      checkOrdersStatus,
      executeOrders,
      getOrders,
      setInitialOrders,
      updateOrdersStatus,
      updateTrader,
    ],
  );

  const closeModal = useCallback(() => setOpen(false), []);

  return { executeTrades, closeModal, open, orders, loading };
};
